import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";

function isVideoMedia(src: string): boolean {
  return /\.(mp4|webm)(?:[?#].*)?$/i.test(String(src || ""));
}

export default function HeroBanner() {
  const { data } = useAdmin();
  const banners = data.banners;
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent(index);
      setIsTransitioning(false);
    }, 300);
  }, []);

  const next = useCallback(() => {
    goTo((current + 1) % banners.length);
  }, [current, goTo, banners.length]);

  const prev = useCallback(() => {
    goTo((current - 1 + banners.length) % banners.length);
  }, [current, goTo, banners.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const banner = banners[current];

  return (
    <section className="relative w-full h-[420px] md:h-[580px] lg:h-[660px] overflow-hidden">
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${isTransitioning ? "opacity-0" : "opacity-100"}`}
      >
        {isVideoMedia(banner.image) ? (
          <video
            key={banner.image}
            src={banner.image}
            className="w-full h-full object-cover object-top"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label={banner.title}
          />
        ) : (
          <img
            src={banner.image}
            alt={banner.title}
            className="w-full h-full object-cover object-top"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
      </div>

      <div
        className={`relative z-10 w-full h-full flex items-center px-4 md:px-8 lg:px-16 transition-all duration-500 ${
          isTransitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
        }`}
      >
        <div className="bg-black/55 rounded-2xl p-6 md:p-8 lg:p-10 max-w-2xl border border-white/10">
          <span className="inline-block bg-primary-500 text-background-50 text-xs font-label font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-4">
            M&amp;Z Materiais para Construção
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
            {banner.title}
          </h1>
          <p className="text-lg md:text-xl font-heading text-primary-300 mb-3">{banner.subtitle}</p>
          <p className="text-background-200 text-sm md:text-base mb-8 max-w-lg">{banner.description}</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to={banner.ctaLink}
              className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-md font-label font-semibold text-sm transition-all whitespace-nowrap cursor-pointer"
            >
              {banner.ctaText}
            </Link>
            <a
              href={data.settings.socialWhatsapp}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 bg-background-50/10 hover:bg-background-50/20 text-background-50 border border-background-50/30 px-6 py-3 rounded-md font-label font-medium text-sm transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line" />
              Fale Conosco
            </a>
          </div>
        </div>
      </div>

      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/30 hover:bg-primary-500 text-background-50 rounded-full transition-all cursor-pointer"
        aria-label="Anterior"
      >
        <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/30 hover:bg-primary-500 text-background-50 rounded-full transition-all cursor-pointer"
        aria-label="Próximo"
      >
        <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {banners.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all cursor-pointer rounded-full ${
              i === current ? "w-8 h-2 bg-primary-500" : "w-2 h-2 bg-background-50/50 hover:bg-background-50"
            }`}
            aria-label={`Banner ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}